import type { ChinaPaymentType } from './china-payment.js';
import { sanitiseMessageContent } from './message-sanitize.js';
import { toIsoTimestamp } from './packages-wire.js';

/**
 * `getalipaylist` / `alipaydetail` → the China Payment request history (MYP-63), the old website's
 * 支付币兑换 → 充值记录 and its 充值详情 page.
 *
 * Checked live on 22 Oct 2026 (MP275466, read-only). `getalipaylist` answers
 * `{data: {count: "2", list: [ … ]}}` with no `code`; `alipaydetail` (`pid` = a row's `id`) answers a
 * bare array holding that one row. Every row carries 35 fields, among them the customer's ID number,
 * name, Alipay/Taobao account and — filled in — a `password`. This file is an **allow-list**: it
 * names the fields it reads and never touches the rest (`userid`, `password`, `alipayaccount`,
 * `alipayaccount2`, `proxy`, `imgsrc`, `qrcodeimg`, `mypoz_transfer_img`, `alibalance`, `bankid`,
 * `submit_type`, `rechargemarking`). The list never reads the request's account details either;
 * only the details view does, from `alipaydetail`.
 *
 * Status codes seen live: `INPROGRESS` (审核中, under review) and `CANCEL` (取消). The code for an
 * approved request has not been seen yet, so it is not guessed: any other code stays `other` and is
 * shown with the vendor's own label.
 */

/** Where a request stands, as far as the codes seen live allow. */
export type ChinaPaymentRecordStatus = 'review' | 'cancelled' | 'other';

/** One China Payment request, as the history list shows it. */
export interface ChinaPaymentRecord {
  readonly id: string;
  /** The request number (`orderno`), e.g. `20261021-977382`. */
  readonly reference?: string;
  /** When it was submitted (`chargetime`), ISO 8601. */
  readonly submittedAt?: string;
  /** The MyPoz account it was paid into (`mybankname`), e.g. `Duitnow`. */
  readonly bankName?: string;
  /** ¥ per RM 1 (`rate`), with the number of decimals it was sent with so it shows as sent. */
  readonly rate?: { readonly value: number; readonly decimals: number };
  /** The RMB amount asked for (`amount`), in cents. */
  readonly rmbCents?: number;
  /** The RM amount paid (`rmamount`), in cents. */
  readonly rmCents?: number;
  /** The request type when it is one the app knows; the raw code otherwise. */
  readonly type?: ChinaPaymentType;
  readonly typeCode?: string;
  /** The vendor's own type name in the reader's language, for a type the app has no label for. */
  readonly typeLabel?: string;
  readonly status: ChinaPaymentRecordStatus;
  readonly statusCode?: string;
  /** The vendor's own status name, for a status the app has no label for. */
  readonly statusLabel?: string;
  /** Staff's reason (`message`), on a cancelled request only. */
  readonly cancelReason?: string;
}

/** The details the customer filled in on the request — the old 充值账号信息 table. */
export interface ChinaPaymentRequestAccount {
  /** `engname` — the name on the request. */
  readonly name?: string;
  /** `idcard` — the ID number, as stored. Mask it before showing it (`maskNric`). */
  readonly idNumber?: string;
  /** `account` — the receiving account's name (bank transfer). */
  readonly accountName?: string;
  /** `cardnumber` — the receiving card/account number (bank transfer). */
  readonly cardNumber?: string;
  /** `bankname` — the receiving bank (bank transfer). */
  readonly bank?: string;
  /** `branch` — the receiving bank's branch (bank transfer). */
  readonly branch?: string;
  /** `sellername` — the seller paid for the customer (proxy pay). */
  readonly sellerName?: string;
  /** `remark` — the customer's own note. */
  readonly remark?: string;
}

/** One request with its account details, from `alipaydetail`. */
export interface ChinaPaymentRequestDetail {
  readonly record: ChinaPaymentRecord;
  /** Undefined when every one of those fields is empty. */
  readonly account?: ChinaPaymentRequestAccount;
}

export interface ChinaPaymentRecordPage {
  /** The vendor's `count` — every request on the account, not just this page. */
  readonly total: number;
  readonly records: readonly ChinaPaymentRecord[];
}

type Row = Readonly<Record<string, unknown>>;

const KNOWN_TYPES: readonly ChinaPaymentType[] = [
  'ALIPAYCHARGE',
  'WXCHARGE',
  'ALIPAY',
  'BANKTRANS',
];

function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** `"111.59"` → 11159. Undefined for anything that is not a plain non-negative amount. */
function cents(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined || !/^\d+(\.\d+)?$/.test(raw)) return undefined;
  return Math.round(Number(raw) * 100);
}

function rate(value: unknown): ChinaPaymentRecord['rate'] {
  const raw = text(value);
  if (raw === undefined || !/^\d+(\.\d+)?$/.test(raw)) return undefined;
  const parsed = Number(raw);
  if (!(parsed > 0)) return undefined;
  return { value: parsed, decimals: raw.split('.')[1]?.length ?? 0 };
}

function toStatus(code: string | undefined): ChinaPaymentRecordStatus {
  switch (code?.toUpperCase()) {
    case 'INPROGRESS':
      return 'review';
    case 'CANCEL':
      return 'cancelled';
    default:
      return 'other';
  }
}

function label(row: Row, field: 'type' | 'status', language: 'zh' | 'en'): string | undefined {
  const cn = text(row[`${field}_cn`]);
  const en = text(row[`${field}_en`]);
  return language === 'zh' ? (cn ?? en) : (en ?? cn);
}

/** The vendor's staff reason, as plain text: it may carry HTML from the back office. */
function reason(value: unknown): string | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;
  const clean = sanitiseMessageContent(raw).trim();
  return clean === '' ? undefined : clean;
}

function toRecord(row: Row, index: number, language: 'zh' | 'en'): ChinaPaymentRecord {
  const reference = text(row['orderno']);
  const submittedAt = toIsoTimestamp(row['chargetime']);
  const bankName = text(row['mybankname']);
  const rateValue = rate(row['rate']);
  const rmbCents = cents(row['amount']);
  const rmCents = cents(row['rmamount']);
  const typeCode = text(row['type']);
  const type = KNOWN_TYPES.find((known) => known === typeCode?.toUpperCase());
  const typeLabel = label(row, 'type', language);
  const statusCode = text(row['status']);
  const status = toStatus(statusCode);
  const statusLabel = label(row, 'status', language);
  const cancelReason = status === 'cancelled' ? reason(row['message']) : undefined;

  return {
    id: text(row['id']) ?? `${reference ?? 'request'}-${index}`,
    ...(reference ? { reference } : {}),
    ...(submittedAt ? { submittedAt } : {}),
    ...(bankName ? { bankName } : {}),
    ...(rateValue ? { rate: rateValue } : {}),
    ...(rmbCents === undefined ? {} : { rmbCents }),
    ...(rmCents === undefined ? {} : { rmCents }),
    ...(type ? { type } : {}),
    ...(typeCode ? { typeCode } : {}),
    ...(typeLabel ? { typeLabel } : {}),
    status,
    ...(statusCode ? { statusCode } : {}),
    ...(statusLabel ? { statusLabel } : {}),
    ...(cancelReason ? { cancelReason } : {}),
  };
}

/**
 * The rows of a list. An object keyed by id loses the vendor's order (JavaScript walks numeric keys
 * in ascending order), so it is put back newest first by id, which is how the vendor sends an array.
 */
function rowsOf(list: unknown): readonly Row[] {
  if (Array.isArray(list)) {
    return list.filter((item): item is Row => typeof item === 'object' && item !== null);
  }
  if (typeof list !== 'object' || list === null) return [];
  return Object.values(list)
    .filter((item): item is Row => typeof item === 'object' && item !== null)
    .sort((a, b) => Number(text(b['id']) ?? 0) - Number(text(a['id']) ?? 0));
}

function count(value: unknown, fallback: number): number {
  const raw = text(value);
  const parsed = raw === undefined ? Number.NaN : Number(raw);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * One page of `getalipaylist` (`{data: {count, list}}`; `list` an array, or an object keyed by id
 * as other endpoints of this vendor send it). The rows stay in the vendor's order, newest first.
 * A body of any other shape reads as an empty page.
 */
export function normaliseChinaPaymentRecords(
  body: unknown,
  language: 'zh' | 'en',
): ChinaPaymentRecordPage {
  const data =
    typeof body === 'object' && body !== null && !Array.isArray(body)
      ? (body as Row)['data']
      : undefined;
  const page = typeof data === 'object' && data !== null ? (data as Row) : {};
  const rows = rowsOf(page['list']);
  const records = rows.map((row, index) => toRecord(row, index, language));
  return { total: count(page['count'], records.length), records };
}

/**
 * `alipaydetail` → one request with the details the customer filled in. Undefined when the vendor
 * answers an empty array (no such request, or not this customer's).
 */
export function normaliseChinaPaymentDetail(
  body: unknown,
  language: 'zh' | 'en',
): ChinaPaymentRequestDetail | undefined {
  const row = rowsOf(Array.isArray(body) ? body : body === undefined ? [] : [body])[0];
  if (!row) return undefined;

  const fields: [keyof ChinaPaymentRequestAccount, unknown][] = [
    ['name', row['engname']],
    ['idNumber', row['idcard']],
    ['accountName', row['account']],
    ['cardNumber', row['cardnumber']],
    ['bank', row['bankname']],
    ['branch', row['branch']],
    ['sellerName', row['sellername']],
    ['remark', row['remark']],
  ];
  const account: Partial<Record<keyof ChinaPaymentRequestAccount, string>> = {};
  for (const [key, value] of fields) {
    const present = text(value);
    if (present !== undefined) account[key] = present;
  }

  const record = toRecord(row, 0, language);
  return Object.keys(account).length > 0 ? { record, account } : { record };
}
